import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ModalWithButtons } from '../../../components/ModalWithButtons/ModalWithButtons.component';
import { TextField } from '@material-ui/core';
import { useStyles, inputProps, inputLabelProps } from './ImageZonesEditor.styles';
import PagesThumbnails from '../../../components/PagesThumbnails/PagesThumbnails.component';
import { thumbNailPage } from '../../../components/PagesThumbnails/PagesThumbnails.types';
import ProtectedImg from '../../../components/ProtectedImg/ProtectedImg.component';
import { PagesNavigator } from '../../../components/PagesNavigator/PagesNavigator.component';
import { ButtonOne } from '../../../components/ButtonOne/ButtonOne.component';
import { SwitchCards, cardDefinition } from './../../../components/SwitchCards/SwitchCards.component';
import ZoneList from './ZonesList/ZoneList.component';
import ClusterList from './ClusterList/ClusterList.component';
import { imageZone, imageZoneWithImgData } from './ZonesList/ZonesList.types';
import ReactCrop, { PixelCrop, PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getUploadedExamSheetFiles, getExamSheetThumbnail } from '../../../services/exams/sessions/sessions.service';
import { AxiosResponse, AxiosError } from 'axios';
import { ExamSheetFile } from '../../../pages/Sessions/SessionsImages/SessionsImagesManagment/SessionImagesManagment.types';
import { Prompt } from '../../../components/Prompt/Prompt.component';
import { ExamTaskImageZone, ExamScoringSchemaImageZone, imageZonesRequest } from '../../../models/imageZone.types';
import {
  updateExamTaskImageZone,
  updateExamScoringSchemaImageZone,
  getTaskImageZones,
  getExamScoringSchemaImageZone,
} from '../../../services/images/images.service';
import { useLayoutContext } from '../../../Context/Layout.context';
import { LeavePrompt } from '../../../components/Prompt/LeavePrompt/LeavePrompt.component';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line
import _ from 'lodash';
import { generateUUid } from '../../../utils/generator';

interface ImageZonesEditorProps {
  examSheetId: string;
  sheetSymbol: string;
  examTaskZone: ExamTaskImageZone;
  examScoringSchemaZone?: ExamScoringSchemaImageZone;
  onCancel: () => void;
  onClose: () => void;
}

const thumbnailsPanelWidth = 165;
const thumbnailsPanelTitleHeight = 20;
const headerAndHooterHeight = 150;
const pageeNavigtoarHeight = 50;
const coordinatesPanelHeight = 200;
const cardsPanelTitleHeight = 40;
const minPercentCropSize = 1;

const defaultPercentCrop: PercentCrop = {
  unit: '%',
  x: 0,
  y: 0,
  width: 100,
  height: 100,
};

const cards = [
  {
    id: 'zones',
    key: 'o',
    caption: 'Obszary',
  },
  {
    id: 'cluster',
    key: 'k',
    caption: 'Klaster',
  },
];

interface examSheetPage {
  file: ExamSheetFile;
  imgData: Blob;
}

const compareFilesByPageNumber = (a: ExamSheetFile, b: ExamSheetFile) => {
  if (a.pageNumber < b.pageNumber) return -1;
  else if (a.pageNumber > b.pageNumber) return 1;
  else return 0;
};

const comparePagesByNo = (a: examSheetPage, b: examSheetPage) => {
  return compareFilesByPageNumber(a.file, b.file);
};

const compareZonesByOrderNumber = (a: imageZone, b: imageZone) => {
  if (a.orderNumber < b.orderNumber) return -1;
  else if (a.orderNumber > b.orderNumber) return 1;
  else return 0;
};

const compareZonesWithDataByOrderNumber = (a: imageZoneWithImgData, b: imageZoneWithImgData) => {
  return compareZonesByOrderNumber(a.zone, b.zone);
};

const ImageZonesEditor: React.FC<ImageZonesEditorProps> = ({
  onClose,
  onCancel,
  examSheetId,
  sheetSymbol,
  examTaskZone,
  examScoringSchemaZone,
}) => {
  const classes = useStyles();
  const imgRef = useRef<HTMLImageElement>(null);
  const { openAlert } = useLayoutContext();
  const { t } = useTranslation();

  const [examSheetFiles, setExamSheetFiles] = useState<ExamSheetFile[]>([]);
  const [examSheetPages, setExamSheetPages] = useState<examSheetPage[]>([]);
  const [thumbNails, setThumbNails] = useState<thumbNailPage[]>([]);
  const [loadedImageZones, setLoadedImageZones] = useState<imageZone[]>([]);
  const [imageZones, setImageZones] = useState<imageZoneWithImgData[]>([]);
  const [currentPageId, setCurrentPageId] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<examSheetPage | undefined>(undefined);
  const [currentZoneIndex, setCurrentZoneIndex] = useState<number | undefined>(undefined);
  const [screenHeight, setScreenHeight] = useState<number>(window.innerHeight);
  const [editorAreaHeight, setEditorAreaHeight] = useState<number>(screenHeight - headerAndHooterHeight);
  const [x1, setX1] = useState<string>(defaultPercentCrop.x.toString());
  const [x2, setX2] = useState<string>(defaultPercentCrop.width.toString());
  const [y1, setY1] = useState<string>(defaultPercentCrop.y.toString());
  const [y2, setY2] = useState<string>(defaultPercentCrop.height.toString());
  const [currentCard, setCurrentCard] = useState<'zones' | 'cluster'>('zones');
  const [percentCrop, setPercentCrop] = useState<PercentCrop>(defaultPercentCrop);
  const [cropIsToSmallWarningOpen, setCropIsToSmallWarningOpen] = useState<boolean>(false);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [isContentChanged, setIsContentChanged] = useState<boolean>(false);
  const [previewNeedsRefresh, setPreviewNeedsRefresh] = useState<boolean>(true);

  useEffect(() => {
    if (examScoringSchemaZone) {
      getExamScoringSchemaImageZone(examScoringSchemaZone.examScoringSchemaId).then(
        (response: AxiosResponse<imageZone[]>) => {
          setLoadedImageZones(response.data.sort(compareZonesByOrderNumber));
        },
        (error: AxiosError) => console.error(error),
      );
    } else {
      getTaskImageZones(examTaskZone.examTaskId).then(
        (response: AxiosResponse<imageZone[]>) => {
          setLoadedImageZones(response.data.sort(compareZonesByOrderNumber));
        },
        (error: AxiosError) => console.error(error),
      );
    }
  }, [examTaskZone.examTaskId]);

  useEffect(() => {
    if (examSheetPages.length && examSheetPages.length === examSheetFiles.length) {
      loadedImageZones.forEach((zone: imageZone) => {
        const page = examSheetPages.find((page: examSheetPage) => page.file.sheetPageId === zone.examSheetPageId);

        if (page) {
          setImageZones((prevState) => {
            return [
              ...prevState,
              {
                zone: { ...zone, tempId: generateUUid(prevState.length.toString()) },
                wholeImageData: page.imgData,
              },
            ];
          });
        }
      });
    }
  }, [loadedImageZones, examSheetPages]);

  useEffect(() => {
    if (examSheetId) {
      getUploadedExamSheetFiles(examSheetId).then(
        (response: AxiosResponse<ExamSheetFile[]>) => {
          setExamSheetFiles(response.data.sort(compareFilesByPageNumber));
        },
        (error: AxiosError) => console.error(error),
      );
    }
  }, [examSheetId]);

  const activatePage = (pageId: number) => {
    setPreviewNeedsRefresh(true);
    setCurrentPageId(pageId);
    setPercentCrop(defaultPercentCrop);
    setTimeout(() => {
      setPreviewNeedsRefresh(false); // in order to reset image size after loading it into preview
    }, 100);
  };

  useEffect(() => {
    setExamSheetPages([]);
    examSheetFiles.forEach((file: ExamSheetFile) => {
      getExamSheetThumbnail(examSheetId, file.pageNumber).then((response: AxiosResponse<ArrayBuffer>) => {
        const imgData = new Blob([response.data]);
        setExamSheetPages((prevState) => {
          return [
            ...prevState,
            {
              file,
              imgData,
            },
          ].sort(comparePagesByNo);
        });
      });
    });
  }, [examSheetFiles]);

  useEffect(() => {
    if (examSheetPages.length) {
      activatePage(examSheetPages[0].file.pageNumber);
    }
    setThumbNails(
      examSheetPages.map((page) => {
        return {
          id: page.file.pageNumber,
          caption: page.file.pageNumber.toString(),
          imgData: page.imgData,
        };
      }),
    );
  }, [examSheetPages]);

  useEffect(() => {
    setX1(Math.round(percentCrop.x).toString());
    setX2(Math.round(percentCrop.x + percentCrop.width).toString());
    setY1(Math.round(percentCrop.y).toString());
    setY2(Math.round(percentCrop.y + percentCrop.height).toString());
  }, [percentCrop]);

  useEffect(() => {
    const updateScreenHeight = () => {
      setScreenHeight(window.innerHeight);
      setEditorAreaHeight(window.innerHeight - headerAndHooterHeight);
    };
    window.addEventListener('resize', updateScreenHeight);
    return () => window.removeEventListener('resize', updateScreenHeight);
  }, []);

  useEffect(() => {
    if (currentPageId !== undefined && examSheetPages.length) {
      const page = examSheetPages.find((page: examSheetPage) => page.file.pageNumber === currentPageId);
      setCurrentPage(page);
    }
  }, [currentPageId, examSheetPages]);

  const getNormalisedValue = (value: string, fallbackValue: number): number => {
    const intValue = !Number.isNaN(parseInt(value)) ? parseInt(value) : fallbackValue;
    if (intValue < 0) {
      return 0;
    } else if (intValue > 100) {
      return 100;
    } else {
      return intValue;
    }
  };

  const onSwithCard = useCallback((card: cardDefinition) => {
    setCurrentCard(card.id as 'zones' | 'cluster');
  }, []);

  const createNewImageZone = async () => {
    if (!currentPage) {
      return;
    }

    if (percentCrop.width < minPercentCropSize || percentCrop.height < minPercentCropSize) {
      setCropIsToSmallWarningOpen(true);
    } else {
      setIsContentChanged(true);
      setImageZones((prevState) => {
        return [
          ...prevState,
          {
            zone: {
              id: null,
              tempId: generateUUid(prevState.length.toString()),
              examSheetPageId: currentPage.file.sheetPageId,
              percentX1: percentCrop.x,
              percentY1: percentCrop.y,
              percentX2: percentCrop.x + percentCrop.width,
              percentY2: percentCrop.y + percentCrop.height,
              orderNumber: prevState.length,
              pageNumber: currentPage.file.pageNumber,
            },
            wholeImageData: currentPage.imgData,
          },
        ];
      });
    }
  };

  const activateZone = useCallback(
    (zone: imageZone) => {
      setPreviewNeedsRefresh(true);
      setCurrentZoneIndex(zone.orderNumber);
      const newPage = examSheetPages.find((page: examSheetPage) => page.file.sheetPageId === zone.examSheetPageId);
      setCurrentPageId(newPage?.file.pageNumber);
      setPercentCrop({
        unit: '%',
        x: zone.percentX1,
        y: zone.percentY1,
        width: zone.percentX2 - zone.percentX1,
        height: zone.percentY2 - zone.percentY1,
      });
      setTimeout(() => {
        setPreviewNeedsRefresh(false); // in order to reset image size after loading it into preview
      }, 100);
    },
    [examSheetPages],
  );

  const removeZone = useCallback(
    (zone: imageZone) => {
      let zoneFound = false;
      let lastZoneBeforeRemoved: imageZone | undefined = undefined;
      let firstZoneAfterRemoved: imageZone | undefined = undefined;
      let currentZoneIndex = 0;
      try {
        setImageZones((prevState) => {
          const newImageZones = prevState.filter((item) => {
            const result = item.zone.orderNumber !== zone.orderNumber;
            if (result) {
              item.zone.orderNumber = currentZoneIndex;
              currentZoneIndex++;
              lastZoneBeforeRemoved = item.zone;
            }
            if (zoneFound && !firstZoneAfterRemoved) {
              firstZoneAfterRemoved = item.zone;
            }
            zoneFound = zoneFound || !result;
            return result;
          });
          return newImageZones;
        });
        setIsContentChanged(true);
        openAlert('success', 'Wybrany obszar został pomyślnie usunięty');
      } catch {
        openAlert('error', 'Usunięcie wybranego obszaru nie powiodło się. Spróbuj ponownie później');
      }

      if (firstZoneAfterRemoved) {
        activateZone(firstZoneAfterRemoved);
      } else if (lastZoneBeforeRemoved) {
        activateZone(lastZoneBeforeRemoved);
      } else {
        setCurrentZoneIndex(undefined);
        activatePage(examSheetFiles[0].pageNumber);
      }
    },
    [examSheetPages, examSheetFiles],
  );

  const handleDropZone = useCallback((draggedZone: imageZone, targetZone: imageZone | null) => {
    if (draggedZone.tempId === targetZone?.tempId) {
      return;
    }
    setImageZones((prevState) => {
      const newImageZones = prevState
        .map((zone) => {
          if (zone.zone.tempId === draggedZone.tempId) {
            return { ...zone, zone: { ...zone.zone, orderNumber: targetZone ? targetZone.orderNumber + 0.1 : -1 } };
          }
          return { ...zone };
        })
        .sort(compareZonesWithDataByOrderNumber)
        .map((zoneWithData: imageZoneWithImgData, index: number) => {
          return {
            ...zoneWithData,
            zone: { ...zoneWithData.zone, orderNumber: index },
          };
        });
      return newImageZones;
    });
    setIsContentChanged(true);
  }, []);

  const changeX1 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setX1(value);
    if (value) {
      const intValue = getNormalisedValue(value, percentCrop.x);
      const newPercentCrop: PercentCrop = {
        unit: '%',
        x: intValue,
        y: percentCrop.y,
        width: parseInt(x2) - intValue,
        height: percentCrop.height,
      };
      setPercentCrop(newPercentCrop);
    }
  };

  const changeX2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setX2(value);
    if (value) {
      const intValue = getNormalisedValue(value, percentCrop.x + percentCrop.width);
      const newPercentCrop: PercentCrop = {
        unit: '%',
        x: percentCrop.x,
        y: percentCrop.y,
        width: intValue - percentCrop.x,
        height: percentCrop.height,
      };
      setPercentCrop(newPercentCrop);
    }
  };

  const changeY1 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setY1(value);
    if (value) {
      const intValue = getNormalisedValue(value, percentCrop.y);
      const newPercentCrop: PercentCrop = {
        unit: '%',
        x: percentCrop.x,
        y: intValue,
        width: percentCrop.width,
        height: parseInt(y2) - intValue,
      };
      setPercentCrop(newPercentCrop);
    }
  };

  const changeY2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setY2(value);
    if (value) {
      const intValue = getNormalisedValue(value, percentCrop.y + percentCrop.height);
      const newPercentCrop: PercentCrop = {
        unit: '%',
        x: percentCrop.x,
        y: parseInt(y1),
        width: percentCrop.width,
        height: intValue - percentCrop.y,
      };
      setPercentCrop(newPercentCrop);
    }
  };

  const previewHeight =
    imgRef?.current && imgRef?.current?.naturalWidth > imgRef?.current?.naturalHeight
      ? editorAreaHeight / 1.41
      : editorAreaHeight;
  const previewRatio = imgRef?.current ? previewHeight / imgRef?.current.naturalHeight : 1;
  const previewWidth =
    !previewNeedsRefresh && imgRef?.current
      ? Math.round(imgRef.current.naturalWidth * previewRatio)
      : editorAreaHeight / 1.41; //1.41 = ratio of A4 format sides
  const handleUpdatingSuccess = () => {
    setIsContentChanged(false);
    openAlert('success', 'Strefa obrazu została dodana pomyślnie');
    onClose();
  };
  const handleUpdatingError = () => {
    openAlert('error', 'Dodanie strefy obrazu nie powiodło się. Spróbuj ponownie później');
  };

  const updateImageZones = () => {
    const zones: imageZonesRequest[] = imageZones.map((zoneWithData: imageZoneWithImgData) => {
      const zone = zoneWithData.zone;

      return {
        percentX1: parseFloat(zone.percentX1.toFixed(2)),
        percentY1: parseFloat(zone.percentY1.toFixed(2)),
        percentX2: parseFloat(zone.percentX2.toFixed(2)),
        percentY2: parseFloat(zone.percentY2.toFixed(2)),
        examSheetPageId: zone.examSheetPageId,
        taskImageZoneId: zone.id,
      };
    });

    if (examScoringSchemaZone) {
      updateExamScoringSchemaImageZone(examScoringSchemaZone.examScoringSchemaId, examSheetId, zones).then(
        handleUpdatingSuccess,
        handleUpdatingError,
      );
    } else {
      updateExamTaskImageZone(examTaskZone.examTaskId, examSheetId, zones).then(
        handleUpdatingSuccess,
        handleUpdatingError,
      );
    }
  };

  const handleCancel = () => {
    if (isContentChanged) {
      setIsLeavePromptOpen(true);
    } else {
      onCancel();
    }
  };

  const handleClickThumbnail = useCallback(
    (pageNo: number, pageIndex: number, pageId: string | number) =>
      activatePage(typeof pageId === 'string' ? parseInt(pageId) : pageId),
    [],
  );

  const handleCropChange = useCallback((crop: PixelCrop, percentCrop: PercentCrop) => setPercentCrop(percentCrop), []);

  const croppingDisabled = !imgRef?.current;
  return (
    <>
      <ModalWithButtons
        open={true}
        size={false}
        navButtonLabel="Strefa obrazu"
        title={`Stwórz strefę obrazu dla arkusza: ${sheetSymbol}`}
        subtitle={`Zadanie: ${examTaskZone.examTaskNumber}${
          examScoringSchemaZone
            ? `, Kryterium:  ${examScoringSchemaZone.criterionNumber}-${examScoringSchemaZone.criterionName} (${examScoringSchemaZone.label})`
            : ''
        }`}
        handleNavBack={handleCancel}
        autoHeight={true}
        onConfirm={updateImageZones}
        onCancel={handleCancel}
        xCloseButton={handleCancel}
        confirmButtonLabel="Zapisz"
        confirmButtonDisabled={!isContentChanged}
        fullScreen={true}
      >
        <div className={classes.editorArea} style={{ height: `${editorAreaHeight}px`, overflowX:'auto' }}>
          <div className={classes.thumbnailsPanel} style={{ width: `${thumbnailsPanelWidth}px` }}>
            <div className={classes.thumbnailsContainer}>
              <div className={classes.panelTitle} style={{ height: `${thumbnailsPanelTitleHeight}px` }}>
                Miniatury stron
              </div>
              <div
                className={classes.thumbnailsScroll}
                style={{ height: `calc(100% - ${thumbnailsPanelTitleHeight}px)` }}
              >
                <PagesThumbnails
                  currentPageId={currentPageId}
                  thumbnailWidth="120px"
                  pages={thumbNails}
                  onClickPage={handleClickThumbnail}
                />
              </div>
            </div>
          </div>
          <div>
            <ReactCrop
              crop={percentCrop}
              onChange={handleCropChange}
              className={classes.pagePreview}
              style={{ width: `${previewWidth}px`, height: `${previewHeight}px` }}
            >
              <ProtectedImg imgRef={imgRef} imgData={currentPage?.imgData} />
            </ReactCrop>
          </div>

          <div className={classes.coordinatesPanel}>
            <div style={{ height: `${pageeNavigtoarHeight}px` }}>
              <PagesNavigator
                totalPages={thumbNails.length}
                currentPage={currentPageId ?? 1}
                setCurrentPage={setCurrentPageId}
              />
            </div>

            <div
              className={classes.coordinatesForm}
              style={{
                height: `${coordinatesPanelHeight}px`,
                marginTop: `${Math.round(editorAreaHeight / 2 - coordinatesPanelHeight / 2 - pageeNavigtoarHeight)}px`,
              }}
            >
              <div className={classes.panelTitle} style={{ marginBottom: '20px' }}>
                Współrzędne:
              </div>
              <div className={classes.coordinatesRow}>
                <div>
                  <TextField
                    value={x1}
                    onChange={changeX1}
                    label="x1"
                    variant="outlined"
                    inputProps={inputProps}
                    InputLabelProps={inputLabelProps}
                    InputProps={{
                      endAdornment: <>%</>,
                    }}
                  />
                </div>
                <div>
                  <TextField
                    value={y1}
                    onChange={changeY1}
                    label="y1"
                    variant="outlined"
                    inputProps={inputProps}
                    InputLabelProps={inputLabelProps}
                    InputProps={{
                      endAdornment: <>%</>,
                    }}
                  />
                </div>
              </div>
              <div className={classes.coordinatesRow}>
                <TextField
                  value={x2}
                  onChange={changeX2}
                  label="x2"
                  variant="outlined"
                  inputProps={inputProps}
                  InputLabelProps={inputLabelProps}
                  InputProps={{
                    endAdornment: <>%</>,
                  }}
                />

                <TextField
                  value={y2}
                  onChange={changeY2}
                  label="y2"
                  variant="outlined"
                  inputProps={inputProps}
                  InputLabelProps={inputLabelProps}
                  InputProps={{
                    endAdornment: <>%</>,
                  }}
                />
              </div>
              <div>
                <ButtonOne
                  className={classes.addZoneButton}
                  variant="contained"
                  color="primary"
                  disabled={croppingDisabled}
                  onClick={createNewImageZone}
                  aria-label="Przycisk dodający nowy obszar"
                >
                  <span style={{ margin: '0px 10px 0px -4px', fontSize: '20px' }}>+</span> DODAJ
                </ButtonOne>
              </div>
            </div>
          </div>
          <div className={classes.zonesPanel}>
            <div style={{ height: `${cardsPanelTitleHeight}px` }}>
              <SwitchCards
                className={classes.zonesClusterSwitchCards}
                cards={cards}
                activeCardId={currentCard}
                onSwitchView={onSwithCard}
              />
            </div>

            {currentCard === 'zones' && (
              <div className={classes.zonesPanelScroll} style={{ height: `calc(100% - ${cardsPanelTitleHeight}px)` }}>
                <ZoneList
                  currentZoneIndex={currentZoneIndex}
                  zones={imageZones}
                  onClickZone={activateZone}
                  onRemoveZone={removeZone}
                  onDropZone={handleDropZone}
                />
              </div>
            )}
            {currentCard === 'cluster' && (
              <div className={classes.zonesPanelScroll} style={{ height: `calc(100% - ${cardsPanelTitleHeight}px)` }}>
                <ClusterList currentZoneIndex={currentZoneIndex} zones={imageZones} onClickZone={activateZone} />
              </div>
            )}
          </div>
        </div>
      </ModalWithButtons>
      {cropIsToSmallWarningOpen && (
        <Prompt
          type="warning"
          open={true}
          mainText="Nie można dodać obszaru o takich współrzędnych."
          subTexts={[{ text: 'Wykadrowany obszar jest zbyt mały.' }]}
          onConfirm={() => setCropIsToSmallWarningOpen(false)}
        />
      )}
      {isLeavePromptOpen && (
        <LeavePrompt isOpen={true} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
      )}
    </>
  );
};

export default ImageZonesEditor;
